export type CheckoutRegistrationPrefill = {
  email?: string
  phoneNumber?: string
  firstName?: string
  lastName?: string
  street?: string
  city?: string
  zipCode?: string
}

export const checkoutRegistrationPrefillParam = 'checkoutPrefill'

const storageKey = 'checkoutRegistrationPrefill'
const maxAgeMs = 3_600_000
const fields: Array<keyof CheckoutRegistrationPrefill> = [
  'email',
  'phoneNumber',
  'firstName',
  'lastName',
  'street',
  'city',
  'zipCode',
]

export const getCheckoutRegistrationUrl = (
  signupUrl: string,
  checkoutId: string | undefined
): string => {
  if (!checkoutId) {
    return signupUrl
  }

  const url = new URL(signupUrl)
  url.searchParams.set(checkoutRegistrationPrefillParam, checkoutId)

  return url.toString()
}

export const saveCheckoutRegistrationPrefill = (
  checkoutId: string,
  data: CheckoutRegistrationPrefill
): void => {
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({ checkoutId, data, createdAt: Date.now() })
    )
  } catch {
    // Registration can still proceed when browser storage is unavailable.
  }
}

export const readCheckoutRegistrationPrefill = (
  sessionId: string
): CheckoutRegistrationPrefill | undefined => {
  try {
    const raw = window.sessionStorage.getItem(storageKey)

    if (!raw) {
      return undefined
    }

    const stored = JSON.parse(raw)

    if (
      !stored ||
      typeof stored.createdAt !== 'number' ||
      Date.now() - stored.createdAt > maxAgeMs ||
      stored.createdAt > Date.now() ||
      typeof stored.checkoutId !== 'string' ||
      !stored.data ||
      typeof stored.data !== 'object'
    ) {
      window.sessionStorage.removeItem(storageKey)

      return undefined
    }

    const checkoutId = new URLSearchParams(window.location.search).get(
      checkoutRegistrationPrefillParam
    )

    if (stored.sessionId !== sessionId) {
      if (stored.sessionId || checkoutId !== stored.checkoutId) {
        return undefined
      }

      // Bind the handoff to this registration before its route drops the query.
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({ ...stored, sessionId })
      )
    }

    return Object.fromEntries(
      fields
        .filter((key) => typeof stored.data[key] === 'string')
        .map((key) => [key, stored.data[key]])
    )
  } catch {
    return undefined
  }
}

export const clearCheckoutRegistrationPrefill = (sessionId: string): void => {
  try {
    const raw = window.sessionStorage.getItem(storageKey)

    if (raw && JSON.parse(raw)?.sessionId === sessionId) {
      window.sessionStorage.removeItem(storageKey)
    }
  } catch {
    // Storage cleanup must not interrupt registration.
  }
}
